/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { CommonModule } from '@angular/common'; // Import CommonModule
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button'; // Import MatButtonModule
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'; // Import MatProgressSpinnerModule
import {
  MatSnackBar,
  MatSnackBarModule,
  MatSnackBarVerticalPosition,
} from '@angular/material/snack-bar'; // Import MatSnackBar and MatSnackBarModule
import { MatTooltipModule } from '@angular/material/tooltip';
import { CompletionMessage, FileDTO, FileUpload } from '@app-libs/model';
import { Subscription } from 'rxjs';
import { AuthService } from '../../../services/auth/auth.service';
import { ChatService } from '../../../services/chat/chat.service';
import { MessageFormatter } from '../../../services/chat/message/message-formatter.service';
import { ChatUIService } from '../../../services/chat/ui/chat-ui.service';
import { ChatErrorHandler } from '../../../services/chat/utils/error-handler.service';
import { ConfigurationClientService } from '../../../services/client/configuration-client.service';
import {
  EventBusService,
  EventTypes,
} from '../../../services/client/event-bus.service';
import { FileImportService } from '../../../services/utils/file-import.service';
import {
  createFileUploadObject,
  formatDisplayFilename,
} from '../../../utils/file-processing.utils';
import {
  extractFileFromDragEvent,
  extractFileFromInputEvent,
  filterNotifications,
  formatNotificationContent,
  getInputPlaceholder,
  getSendButtonIcon,
  hasMessageContent,
  isMobileDevice,
  prepareUserMessage,
  type Notification
} from '../../../utils/input-handling.utils';
import { AudioRecorderComponent } from './audio-recorder/audio-recorder.component';
import { InputActionsComponent } from './input-actions/input-actions.component';
import { TextInputComponent } from './text-input/text-input.component';


@Component({
  selector: 'app-chat-input',
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TextInputComponent,
    AudioRecorderComponent,
    InputActionsComponent,
  ],
  templateUrl: './chat-input.component.html',
  styleUrls: ['./chat-input.component.scss'],
  animations: [
    trigger('fade', [
      state('void', style({ opacity: 0 })),
      transition(':enter', [animate('300ms ease-in', style({ opacity: 1 }))]),
      transition(':leave', [animate('0ms ease-out', style({ opacity: 0 }))]),
    ]),
    trigger('pulsate', [
      state('pulsate', style({ transform: 'scale(1)' })),
      transition('* => pulsate', [
        animate('0.5s ease-in-out', style({ transform: 'scale(1.1)' })),
        animate('0.5s ease-in-out', style({ transform: 'scale(1)' })),
      ]),
    ]),
  ],
})
export class ChatInputComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly ATTACH_MESSAGE = 'Attach pdf';
  private readonly PLACEHOLDER = 'Ask me anything...';
  private readonly DEFAULT_SEND_ICON = 'arrow_upward';

  @Output() sendMessage = new EventEmitter<CompletionMessage>();
  @Output() scrollBottom = new EventEmitter<boolean>();

  sendButtonIcon: string = this.DEFAULT_SEND_ICON;
  showScrollButton = false;
  userMessage = '';
  placeholder: string = this.PLACEHOLDER;
  fileTooltip: string = this.ATTACH_MESSAGE;
  isUploadButtonDisabled = false;
  isMobile: boolean = isMobileDevice();
  isImageFile = false;
  acceptFileTypes = 'image/*, application/pdf';
  notifications: Notification[] = [];
  private subscriptions: Subscription = new Subscription();

  @ViewChild('textInputComponent') textInputComponent!: any;
  @ViewChild('inputActionsComponent') inputActionsComponent!: InputActionsComponent;

  constructor(
    private snackBar: MatSnackBar,
    public chatService: ChatService,
    private fileImportService: FileImportService,
    private cdr: ChangeDetectorRef,
    private eventBus: EventBusService,
    private authService: AuthService,
    public configService: ConfigurationClientService,
    private messageFormatter: MessageFormatter,
    private errorHandler: ChatErrorHandler,
    private uiService: ChatUIService
  ) { }

  ngAfterViewInit(): void {
    // Focus the chat input text area after the view initializes
    if (!this.isMobile) {
      this.focusInput();
      // Reset file inputs to ensure they are empty
      this.resetFileInputs();
    }
  }

  get state() {
    return this.chatService.state;
  }

  ngOnInit() {
    this.subscribeToEvent(EventTypes.REMOVE_UPLOADED_FILE, () => this.clearUploadFile());
    this.subscribeToEvent(EventTypes.SEND_USER_MESSAGE, () => this.chatService.setProcessing(true));
    this.subscribeToEvent(EventTypes.STREAM_COMPLETE, () => this.chatService.setProcessing(false));
    this.subscribeToEvent(EventTypes.STREAM_FIRST_CHUNK_RECEIVED, () => this.clearUploadFile());

    // Subscribe to ChatUIService file upload state to update UI
    this.subscriptions.add(
      this.uiService.uploadedFile$.subscribe(uploadedFile => {
        if (uploadedFile) {
          // Update fileTooltip when file is uploaded
          this.fileTooltip = uploadedFile.file_name;
          this.cdr.detectChanges();
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  private subscribeToEvent(eventType: EventTypes, handler: (data: any) => void) {
    this.subscriptions.add(
      this.eventBus.on(eventType).subscribe((event) => handler(event.data))
    );
  }

  removeUploadedFile() {
    this.eventBus.emit({ type: EventTypes.REMOVE_UPLOADED_FILE });
  }

  onSendUserMessage(data: any) {
    this.chatService.setProcessing(true);
  }

  onStreamComplete(data: any) {
    this.chatService.setProcessing(false);
  }

  onFirstChunkReceived(data: any) {
    this.clearUploadFile();
  }

  uploadButtonDisabled(value = false) {
    this.isUploadButtonDisabled = value;
    this.cdr.detectChanges();
  }

  onKeydown(event: KeyboardEvent) {
    // This method is no longer needed as TextInputComponent handles submission
    // Keep it empty for potential future use or other keyboard shortcuts
  }

  onTextInputKeydown(event: KeyboardEvent) {
    // Handle any additional keydown logic if needed
    // Don't call submitForm here as TextInputComponent already handles it
    this.onKeydown(event);
  }

  onTextInputSubmit() {
    this.submitForm();
  }

  onUserMessageChange(message: string) {
    this.userMessage = message;
  }

  submitForm() {
    // console.debug('SubmitForm message: ', this.userMessage);

    // Check if processing is active - prevent multiple submissions
    if (this.state.processing) {
      return;
    }

    this.clearNotifications();

    // Check if the user message is empty
    const preparedMessage = prepareUserMessage(this.userMessage);
    if (!preparedMessage) {
      this.showCustomSnackbar('Please type your question above', '', 1000);
      this.focusInput();
      return;
    }

    this.sendUserMessage(preparedMessage);
  }

  sendUserMessage(content: string) {
    const message: Partial<CompletionMessage> = {
      content,
      user_id: this.authService.getCurrentUserId()!,
      role: 'user',
      created_at: new Date().toISOString(),
      uploaded_file: this.chatService.getUploadedFile() || undefined,
    };

    // Validate message before sending (basic validation for UI)
    if (!content.trim()) {
      this.showCustomSnackbar('Message cannot be empty', 'Close', 2000);
      return;
    }

    // Additional validation using MessageFormatter (UI-level validation)
    const validation = this.messageFormatter.validateUIMessage(message);
    if (!validation.isValid) {
      const errorMessage = validation.errors.join(', ');
      this.showCustomSnackbar(`Invalid message: ${errorMessage}`, 'Close', 4000);
      return;
    }

    // Show warnings if any
    if (validation.warnings.length > 0) {
      validation.warnings.forEach(warning => {
        this.showCustomSnackbar(warning, 'Close', 3000);
      });
    }

    this.eventBus.emit({ type: EventTypes.SEND_USER_MESSAGE, data: message });
    this.sendMessage.emit(message as CompletionMessage);
    this.userMessage = '';

    if (!isMobileDevice()) {
      this.focusInput();
    }
  }

  focusInput() {
    if (this.textInputComponent) {
      this.textInputComponent.focusInput();
    }
  }

  showComingSoonMessage() {
    this.showCustomSnackbar('Coming soon!');
  }

  uploadFile() {
    // Handled by InputActionsComponent
  }

  captureImage() {
    // Handled by InputActionsComponent
  }

  testFunction() {
    console.log('testFunction');
  }

  onFileSelected(event: Event) {
    console.debug('onFileSelected', event);
    const file = extractFileFromInputEvent(event);
    if (!file) {
      console.log('File selection was canceled');
      this.removeUploadedFile();
      return;
    }

    this.handleFile(file);
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    const file = extractFileFromDragEvent(event);
    if (file) {
      this.handleFile(file);
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  async handleFile(file: File) {
    console.debug('ChatInputComponent.handleFile - delegating to ChatUIService');
    this.uploadButtonDisabled(true);
    try {
      // Delegate to ChatUIService which handles file upload through FileUploadService
      await this.uiService.handleFileUpload(file);
      // The FileUploadService will emit the FILE_UPLOADED event and handle state
      // We don't need handleFileContent anymore as FileUploadService handles everything
    } catch (error) {
      // Use ChatErrorHandler for better error handling
      const chatError = this.errorHandler.handleError(error);
      const displayMessage = this.errorHandler.formatErrorForDisplay(chatError);
      this.showCustomSnackbar(displayMessage, 'Close', 5000);

      // Report error for monitoring
      this.errorHandler.reportError(chatError).subscribe();
    } finally {
      this.uploadButtonDisabled(false);
    }
  }

  handleFileContent(uploaded: Partial<FileDTO>, file: File) {
    const fileUpload = this.createFileUploadObject(uploaded, file);
    this.saveUploadFile(fileUpload);
  }

  private saveUploadFile(fileUpload: FileUpload) {
    console.debug('saveUploadFile', fileUpload);
    this.chatService.setUploadedFile(fileUpload);
    this.eventBus.emit({ type: EventTypes.FILE_UPLOADED, data: fileUpload });
    this.fileTooltip = `${fileUpload.file_name}`;

    // Force change detection to ensure the uploaded file displays
    this.cdr.detectChanges();

    // Debug log to verify the state
    console.debug('saveUploadFile: state.uploadedFile =', this.state.uploadedFile);

    // this.addNotification({
    //   content: this.fileTooltip,
    //   button: 'clear',
    //   action: () => this.removeUploadedFile(),
    //   full_click: true,
    //   sticky: true,
    // });
  }

  private clearUploadFile() {
    console.debug('clearUploadFile');
    this.chatService.setUploadedFile(undefined);
    this.resetFileInputs();
    this.clearNotifications(true);
    this.cdr.detectChanges();
  }

  showCustomSnackbar(
    tooltip: string,
    action = 'Close',
    duration = 2000,
    position: 'bottom' | 'top' = 'bottom'
  ): void {
    // panelClass: ['custom-snackbar']
    const options = {
      duration: duration,
      verticalPosition: position as MatSnackBarVerticalPosition,
      panelClass: ['custom-snackbar'],
    };
    this.snackBar.open(tooltip, action, options);
  }

  createFileUploadObject(uploaded: Partial<FileDTO>, file: File): FileDTO {
    return createFileUploadObject(
      uploaded,
      file,
      this.authService.getCurrentUserId()!
    );
  }


  scrollToBottom() {
    console.log('Firing scrollToBottom force: true');
    this.eventBus.emit({ type: EventTypes.SCROLL_BOTTOM, data: true });
  }

  private resetFileInputs() {
    this.fileTooltip = this.ATTACH_MESSAGE;
    this.placeholder = this.PLACEHOLDER;
    if (this.inputActionsComponent) {
      this.inputActionsComponent.resetFileInputs();
    }
  }


  addNotification(notification: Notification, clear = true): void {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    clear && this.clearNotifications();
    notification.content = formatNotificationContent(notification.content, this.isMobile, 8, 60);

    // Stopped, ugly on mobile with new buttons
    if (!this.isMobile) {
      this.notifications.push(notification);
    }
  }

  clearNotifications(include_sticky = false): void {
    this.notifications = filterNotifications(this.notifications, include_sticky);
  }

  removeNotification(index: number) {
    this.notifications.splice(index, 1);
  }


  stopProcessing() {
    console.debug('Stop processing...');
    this.eventBus.emit({ type: EventTypes.STOP_STREAM });
    this.chatService.stopStream();
  }

  newChatClicked() {
    console.debug('New chat clicked');
    this.eventBus.emit({ type: EventTypes.NEW_CHAT });
  }

  disableRightClick(event: MouseEvent): void {
    event.preventDefault();
  }

  // Audio recording handlers
  onRecordingStarted(): void {
    this.sendButtonIcon = getSendButtonIcon(true, hasMessageContent(this.userMessage), this.DEFAULT_SEND_ICON, 'graphic_eq');
    this.placeholder = getInputPlaceholder(true, false, this.PLACEHOLDER);
    this.cdr.detectChanges();
  }

  onRecordingStopped(): void {
    this.sendButtonIcon = getSendButtonIcon(false, hasMessageContent(this.userMessage), this.DEFAULT_SEND_ICON, 'graphic_eq');
    this.placeholder = getInputPlaceholder(false, false, this.PLACEHOLDER);
    this.cdr.detectChanges();
  }

  onAudioRecorded(audioBlob: Blob): void {
    // Begin transcription process
    this.placeholder = getInputPlaceholder(false, true, this.PLACEHOLDER);
    this.cdr.detectChanges();

    this.chatService
      .transcribeAudio(audioBlob)
      .then((transcription) => {
        console.log('Transcription:', transcription);
        this.userMessage = transcription;
        this.cdr.detectChanges();

        // Focus the input after transcription
        if (!isMobileDevice()) {
          this.focusInput();
        }
      })
      .catch((error) => {
        // Use ChatErrorHandler for better error handling
        const chatError = this.errorHandler.handleError(error);
        const displayMessage = this.errorHandler.formatErrorForDisplay(chatError);
        this.showCustomSnackbar(displayMessage, 'Close', 5000, 'bottom');

        // Report error for monitoring
        this.errorHandler.reportError(chatError).subscribe();

        // Suggest user action if available
        const actionSuggestion = this.errorHandler.getUserActionSuggestion(chatError);
        if (actionSuggestion) {
          setTimeout(() => {
            this.showCustomSnackbar(actionSuggestion, 'Close', 3000, 'bottom');
          }, 1000);
        }
      })
      .finally(() => {
        this.placeholder = getInputPlaceholder(false, false, this.PLACEHOLDER);
        this.cdr.detectChanges();
      });
  }

  onRecordingError(error: string): void {
    this.showCustomSnackbar(error, 'Close', error.includes('short') ? 2000 : 5000, 'bottom');
  }


  getFormattedFileName(fileName: string): string {
    const maxLength = this.isMobile ? 30 : 80;
    return formatDisplayFilename(fileName, maxLength);
  }
}
